<div
  class="camera-schedule"
  [ngClass]="{
    'camera-schedule__changed':
      cameraScheduleService.isFilledAndChanged() &&
      !readonly() &&
      (cameraScheduleService.isChanged$ | async),
    'camera-schedule__disabled': isDisabled() && !readonly(),
  }"
>
  <div class="camera-schedule__header">
    <h2 class="camera-schedule__title">
      {{ title() }}
      @if (readonly()) {
        <button
          mat-icon-button
          (click)="onExportJson()"
          [matTooltip]="strings.tooltip.exportScheduleJson"
          [disabled]="!canExportJson()"
          [attr.data-testid]="'export-schedule-button'"
          color="accent"
        >
          <mat-icon>file_download</mat-icon>
        </button>
      }
      @if (!readonly()) {
        <mat-icon
          (mouseover)="onTooltipMouseEnter()"
          (mouseleave)="onTooltipMouseLeave()"
          (focus)="onTooltipMouseEnter()"
          (blur)="onTooltipMouseLeave()"
          tabindex="0"
          aria-label="Camera schedule help"
          cdkOverlayOrigin
          #trigger="cdkOverlayOrigin"
        >
          help_outline
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="trigger"
            [cdkConnectedOverlayOpen]="isOpen()"
          >
            <div
              class="tooltip-content"
              (mouseenter)="onTooltipMouseEnter()"
              (mouseleave)="onTooltipMouseLeave()"
              (focusin)="onTooltipMouseEnter()"
              (focusout)="onTooltipMouseLeave()"
            >
              <ul>
                <li>{{ strings.tooltip.permanentRules }}</li>
                <li>{{ strings.tooltip.temporaryRules }}</li>
                <li>{{ strings.tooltip.simultaneousRules }}</li>
                <li>
                  {{ strings.tooltip.questions }}
                  <a [href]="strings.tooltip.consultManualUrl" (click)="onClickConsult($event)">{{
                    strings.tooltip.consultManual
                  }}</a>
                  {{ strings.tooltip.or }}
                  <a [href]="strings.tooltip.contactUrl" (click)="onClickContact($event)">{{
                    strings.tooltip.contactSpecialist
                  }}</a>
                </li>
              </ul>
            </div>
          </ng-template>
        </mat-icon>
      }
    </h2>
    <div>
      @if (showScheduleRemove() && cameraScheduleService.isFilledAndChanged() && !readonly()) {
        <button
          mat-icon-button
          (click)="onClickRemove()"
          [matTooltip]="strings.buttons.discardChanges"
          [disabled]="isDisabled()"
        >
          <mat-icon color="warn">close</mat-icon>
        </button>
      }
      @if (!readonly() && showRemoveSchedule() && !cameraScheduleService.isEmptySchedule) {
        <button
          mat-button
          (click)="onClickRemoveCamera()"
          [matTooltip]="strings.buttons.removeExistingCameraSchedule"
          [disabled]="isDisabled()"
        >
          <mat-icon [color]="!cameraScheduleService.isEmptySchedule ? 'warn' : undefined"
            >delete
          </mat-icon>
          {{ strings.buttons.removeCameraSchedule }}
        </button>
      }

      @if (showReset() && !readonly() && !isDisabled()) {
        <button
          mat-icon-button
          (click)="onClickRemoveCamera()"
          [matTooltip]="strings.buttons.resetScheduleToFactory"
          [disabled]="isDisabled()"
        >
          <mat-icon color="warn">undo</mat-icon>
        </button>
      }
    </div>
  </div>
  @if (!readonly() && cameraScheduleService.errors()?.length) {
    <div class="camera-schedule__errors">
      @for (error of cameraScheduleService.errors(); track error) {
        <vectronic-alert type="error">
          <span>{{ error }}</span>
        </vectronic-alert>
      }
    </div>
  }
  @for (warning of warningMessages(); track warning) {
    <vectronic-alert type="warning">{{ warning }}</vectronic-alert>
  }
  @if (showOverrideWarning() && !cameraScheduleService.isEmptySchedule) {
    <div class="camera-schedule__override-warning">
      <vectronic-alert type="warning">
        {{ strings.warnings.overwriteWarning }}
      </vectronic-alert>
    </div>
  }
  @if (
    cameraScheduleService.isEmptySchedule &&
    ((cameraScheduleService.isChanged$ | async) || readonly())
  ) {
    <div>{{ strings.warnings.removingScheduleDescription }}</div>
    <vectronic-alert type="warning">
      {{ strings.warnings.removingScheduleWarning }}
    </vectronic-alert>
  }
  @if (
    !hideRules() &&
    (cameraScheduleService.isScheduleExpanded() ||
      readonly() ||
      (cameraScheduleService.hasDefaultValue() && !cameraScheduleService.isEmptySchedule))
  ) {
    <div class="camera-schedule__wrapper" [attr.data-testid]="'camera-schedule'">
      <div class="camera-schedule__left-side">
        @let indices =
          hideChangedSchedules()
            ? cameraScheduleService.defaultRulesIndexes$.value
            : cameraScheduleService.rulesIndexes$.value;

        @for (ruleIndex of indices; track ruleIndex; let index = $index) {
          <rc-camera-rule
            (remove)="onRemoveRule($event)"
            [index]="ruleIndex"
            [readonly]="readonly() || isDisabled()"
            [videoFormatsOptions]="videoFormatsOptions()"
            [photoFormatsOptions]="photoFormatsOptions()"
            [order]="index"
            [hideChangedSchedules]="hideChangedSchedules()"
            [isRulesDeletable]="isRulesDeletable()"
            [hideCameraFormat]="hideCameraFormat()"
            [hasHighActivityCameraTrigger]="hasHighActivityCameraTrigger()"
          ></rc-camera-rule>
        }
        @if (!readonly() && !isDisabled() && !cameraScheduleService.isEmptySchedule) {
          <div class="camera-schedule__action-button">
            <button
              mat-button
              [attr.data-testid]="'add-rule-button'"
              class="camera-schedule__rule-action"
              (click)="onClickAddRule()"
              [disabled]="isDisabled()"
            >
              <mat-icon color="accent">add</mat-icon>
              {{ strings.buttons.addRule }}
            </button>
            @if (
              !readonly() && (showLoadFromTemplates() || showImportJson() || showLoadGpsXFile())
            ) {
              <button
                mat-button
                cdkOverlayOrigin
                #loadTriggerExpanded="cdkOverlayOrigin"
                [matMenuTriggerFor]="loadScheduleMenu"
                (click)="onLoadMenuClick(loadTriggerExpanded)"
                [attr.data-testid]="'load-schedule-button'"
                [disabled]="isDisabled()"
              >
                {{ strings.buttons.loadSchedule }}
                <mat-icon color="accent">expand_more</mat-icon>
              </button>
            }
            @if (
              !readonly() &&
              cameraScheduleService.isFilled() &&
              cameraScheduleService.isValid() &&
              (showSaveAsTemplate() || canExportJson())
            ) {
              <button
                mat-button
                cdkOverlayOrigin
                #saveTrigger="cdkOverlayOrigin"
                [matMenuTriggerFor]="saveScheduleMenu"
                (click)="onSaveMenuClick(saveTrigger)"
                [attr.data-testid]="'save-schedule-button'"
                [disabled]="isDisabled()"
              >
                {{ strings.buttons.saveAs }}
                <mat-icon color="accent">expand_more</mat-icon>
              </button>
            }
          </div>
        }
      </div>
      @if (cameraScheduleService.isFilled() && !cameraScheduleService.isEmptySchedule) {
        <div class="camera-schedule__preview">
          <div class="camera-schedule__calendar">
            <rc-camera-calendar [(showPreview)]="showPreview"></rc-camera-calendar>
          </div>
          <!--<rc-camera-lifetime></rc-camera-lifetime>-->
        </div>
      }
    </div>
  } @else if (!hideRules()) {
    <div class="camera-schedule__action-button">
      <button
        mat-button
        (click)="onAddSchedule()"
        [attr.data-testid]="'add-schedule-button'"
        [disabled]="isDisabled()"
      >
        @if (cameraScheduleService.isEmptySchedule) {
          {{ strings.buttons.addRule }}
        } @else {
          {{ strings.buttons.addCameraSchedule }}
        }
        <mat-icon color="accent">add</mat-icon>
      </button>
      @if (
        (showLoadFromTemplates() || showImportJson() || showLoadGpsXFile()) &&
        !cameraScheduleService.isEmptySchedule
      ) {
        <button
          mat-button
          cdkOverlayOrigin
          #loadTriggerCollapsed="cdkOverlayOrigin"
          [matMenuTriggerFor]="loadScheduleMenu"
          (click)="onLoadMenuClick(loadTriggerCollapsed)"
          [attr.data-testid]="'load-schedule-button'"
          [disabled]="isDisabled()"
        >
          {{ strings.buttons.loadSchedule }}
          <mat-icon color="accent">expand_more</mat-icon>
        </button>
      }
    </div>
  }
  <div class="camera-schedule__submit-reset-button">
    @if (cameraScheduleService.isFilledAndChanged() && showSubmit() && !isDisabled()) {
      <button mat-button (click)="onSubmit()" [disabled]="isDisabled()">
        {{ strings.buttons.submit }}
        <mat-icon color="accent">send</mat-icon>
      </button>
    }
  </div>

  @if (
    !isMaxSchedulesReachedAndIsNotFilled() &&
    isDisabled() &&
    remoteSchedules()?.CAMERA?.length &&
    !readonly() &&
    !isMoreThanOneCameraType()
  ) {
    <vectronic-alert type="error">
      {{ strings.errors.scheduleNotAvailable }}
      {{ remoteSchedules()?.CAMERA?.join(',') }}
    </vectronic-alert>
  }

  @if (isMaxSchedulesReachedAndIsNotFilled() && !readonly() && !isDisabledByEmptySchedule()) {
    <vectronic-alert type="error">
      {{ strings.errors.canNotBeCombined(collarsWithShortHeaderFormatJoin()) }}
    </vectronic-alert>
  }

  @if (isMoreThanOneCameraType() && !readonly()) {
    <vectronic-alert type="error">
      {{ strings.errors.moreThanOneCameraType }}
    </vectronic-alert>
  }
</div>

<input
  #importJsonInput
  type="file"
  accept=".json,application/json"
  hidden
  (change)="onImportJsonSelected($event)"
/>
<input
  #gpsXFileInput
  type="file"
  [accept]="gpsXFileAccept()"
  hidden
  (change)="onLoadGpsXFileSelected($event)"
/>

<mat-menu #loadScheduleMenu="matMenu">
  @if (showLoadFromTemplates()) {
    <button mat-menu-item (click)="onOpenTemplateSelector()">
      <mat-icon color="accent">inventory_2</mat-icon>
      {{ strings.buttons.loadFromTemplate }}
    </button>
  }
  @if (showImportJson()) {
    <button mat-menu-item (click)="onImportJson()">
      <mat-icon color="accent">upload_file</mat-icon>
      {{ strings.buttons.importJson }}
    </button>
  }
  @if (showLoadGpsXFile()) {
    <button mat-menu-item (click)="onLoadGpsXFile()" [disabled]="gpsXFileImportService.loading()">
      <mat-icon color="accent">upload_file</mat-icon>
      {{ strings.buttons.loadGpsXFile }}
    </button>
  }
</mat-menu>

<mat-menu #saveScheduleMenu="matMenu">
  @if (showSaveAsTemplate()) {
    <button mat-menu-item (click)="onOpenSaveTemplate()">
      <mat-icon color="accent">save</mat-icon>
      {{ strings.buttons.saveAsTemplate }}
    </button>
  }
  <button mat-menu-item (click)="onExportJson()" [attr.data-testid]="'export-schedule-button'">
    <mat-icon color="accent">file_download</mat-icon>
    {{ strings.buttons.exportJson }}
  </button>
</mat-menu>

@if (showLoadFromTemplates()) {
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="loadScheduleTrigger()"
    [cdkConnectedOverlayOpen]="isLoadScheduleOpen()"
    (overlayOutsideClick)="isLoadScheduleOpen.set(false)"
  >
    <div class="camera-schedule__load-overlay">
      <rc-schedule-template-selector
        [scheduleType]="'CAMERA'"
        (templateSelected)="onTemplateSelected($event)"
      ></rc-schedule-template-selector>
    </div>
  </ng-template>
}

@if (isSaveScheduleOpen()) {
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="saveScheduleTrigger()"
    [cdkConnectedOverlayOpen]="isSaveScheduleOpen()"
    (overlayOutsideClick)="isSaveScheduleOpen.set(false)"
  >
    <div class="camera-schedule__load-overlay camera-schedule__save-overlay">
      <mat-form-field appearance="outline">
        <mat-label>{{ strings.form.templateNameLabel }}</mat-label>
        <input
          matInput
          [ngModel]="templateName()"
          (ngModelChange)="templateName.set($event)"
          (keydown.enter)="onConfirmSaveTemplate()"
        />
      </mat-form-field>
      <button
        mat-button
        (click)="onConfirmSaveTemplate()"
        [disabled]="!templateName() || isDisabled()"
        color="accent"
      >
        {{ strings.buttons.saveTemplate }}
      </button>
    </div>
  </ng-template>
}
